import React, { useState, useEffect } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getMyProjects } from '../../services/projectService';
import { getAssignmentsByProject, createAssignment, updateAssignment, getAssignmentSubmissions, gradeSubmission, markAssignmentComplete, markAssignmentOutcome } from '../../services/assignmentService';
import { getClassesByProject } from '../../services/classService';
import { formatDate, formatDateTime } from '../../utils/helpers';
import useForm from '../../hooks/useForm';
import { useToast } from '../../context/ToastContext';
import styles from './MentorAssignments.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';
import AssignmentChecklist from '../../components/common/AssignmentChecklist/AssignmentChecklist';

// Same colours STATUS_COLORS gives these statuses, so the button a mentor
// presses and the badge that appears afterwards are the same colour.
const OUTCOME_COLORS = {
  completed: '#388E3C',   // green
  half_done: '#FF9800',   // orange
  not_done: '#F44336',    // red
};

const OUTCOME_LABELS = {
  half_done: 'Half Done / Incomplete',
  not_done: 'Not Done',
};

const MentorAssignments = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [currentProject, setCurrentProject] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGradingModal, setShowGradingModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [editValues, setEditValues] = useState(null);
  const [completeValues, setCompleteValues] = useState(null);
  const [outcomeValues, setOutcomeValues] = useState(null);   // { assignment, status, note }
  const [saving, setSaving] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedAssignmentData, setSelectedAssignmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gradeValues, setGradeValues] = useState({
    score: '',
    feedback: '',
    return_for_resubmission: false,
    new_deadline: ''
  });
  const toast = useToast();

  const isProjectFrozen = () => {
    return currentProject && (currentProject.status === 'on_hold' || currentProject.status === 'cancelled');
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchAssignments(selectedProject);
      fetchClasses(selectedProject);
      const project = projects.find(p => p.id === selectedProject);
      setCurrentProject(project);
    } else {
      setCurrentProject(null);
      setClasses([]);
    }
  }, [selectedProject, projects]);

  const fetchProjects = async () => {
    try {
      const data = await getMyProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async (projectId) => {
    try {
      const data = await getAssignmentsByProject(projectId);
      setAssignments(data);
      
      // Fetch submissions for each assignment
      const submissionsData = {};
      for (const assignment of data) {
        try {
          const subs = await getAssignmentSubmissions(assignment.id);
          submissionsData[assignment.id] = subs;
        } catch (error) {
          submissionsData[assignment.id] = [];
        }
      }
      setAssignmentSubmissions(submissionsData);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const fetchClasses = async (projectId) => {
    try {
      const data = await getClassesByProject(projectId);
      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const { values, handleChange, handleSubmit, resetForm , isSubmitting} = useForm(
    {
      title: '',
      description: '',
      assigned_date: new Date().toISOString().split('T')[0],
      deadline: '',
      max_score: 0,
      class_id: ''
    },
    async (formValues) => {
      try {
        await createAssignment({ ...formValues, project_id: selectedProject });
        setShowCreateModal(false);
        resetForm();
        fetchAssignments(selectedProject);
        toast.success('Assignment created successfully!');
      } catch (error) {
        toast.error('Error creating assignment: ' + error.message);
      }
    }
  );

  const handleOpenEdit = (assignment) => {
    setEditValues({
      id: assignment.id,
      title: assignment.title || '',
      description: assignment.description || '',
      class_id: assignment.class_id || '',
      assigned_date: assignment.assigned_date ? String(assignment.assigned_date).split('T')[0] : '',
      deadline: assignment.deadline ? String(assignment.deadline).split('T')[0] : '',
      max_score: assignment.max_score ?? 0
    });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateAssignment = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateAssignment(editValues.id, {
        title: editValues.title,
        description: editValues.description,
        class_id: editValues.class_id || null,
        assigned_date: editValues.assigned_date,
        deadline: editValues.deadline,
        max_score: parseInt(editValues.max_score, 10) || 0
      });
      setShowEditModal(false);
      setEditValues(null);
      fetchAssignments(selectedProject);
      toast.success('Assignment updated successfully!');
    } catch (error) {
      toast.error('Error updating assignment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenComplete = (assignment) => {
    setCompleteValues({ assignment, score: '', feedback: '' });
    setShowCompleteModal(true);
  };

  const handleMarkComplete = async () => {
    const { assignment, score, feedback } = completeValues;
    if (assignment.max_score > 0 && (score === '' || score === null)) {
      toast.warning('Please enter a score to mark this assignment as completed');
      return;
    }
    setSaving(true);
    try {
      await markAssignmentComplete(assignment.id, {
        score: assignment.max_score > 0 ? parseInt(score, 10) : null,
        feedback
      });
      setShowCompleteModal(false);
      setCompleteValues(null);
      fetchAssignments(selectedProject);
      toast.success('Assignment marked as completed!');
    } catch (error) {
      toast.error('Error marking assignment complete: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenOutcome = (assignment, status) => {
    setOutcomeValues({ assignment, status, note: '' });
  };

  // No score, unlike completing: see mark_assignment_outcome on the backend.
  const handleMarkOutcome = async () => {
    const { assignment, status, note } = outcomeValues;
    setSaving(true);
    try {
      await markAssignmentOutcome(assignment.id, status, note.trim() || null);
      setOutcomeValues(null);
      fetchAssignments(selectedProject);
      toast.success(`Marked as ${OUTCOME_LABELS[status].toLowerCase()}.`);
    } catch (error) {
      toast.error('Could not mark the assignment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleViewSubmission = (assignment) => {
    const submissions = assignmentSubmissions[assignment.id] || [];
    if (submissions.length === 0) {
      toast.info('No submissions yet');
      return;
    }
    
    setSelectedAssignmentData(assignment);
    setSelectedSubmission(submissions[0]); // Get the first (latest) submission
    
    // Set initial grade values
    setGradeValues({
      score: submissions[0].score || '',
      feedback: submissions[0].feedback || '',
      return_for_resubmission: false,
      new_deadline: ''
    });
    
    setShowGradingModal(true);
  };

  const handleGradeSubmission = async (returnForResubmission) => {
    try {
      const gradeData = {
        return_for_resubmission: returnForResubmission,
        feedback: gradeValues.feedback
      };
      
      // Add score if provided (for both completion and return with partial credit)
      if (selectedAssignmentData.max_score > 0 && gradeValues.score && gradeValues.score !== '') {
        gradeData.score = parseInt(gradeValues.score);
      }
      
      // Validate score required for completion (but optional for return)
      if (!returnForResubmission && selectedAssignmentData.max_score > 0) {
        if (!gradeValues.score || gradeValues.score === '') {
          toast.warning('Please enter a score to complete the assignment');
          return;
        }
      }
      
      if (returnForResubmission && gradeValues.new_deadline) {
        gradeData.new_deadline = new Date(gradeValues.new_deadline).toISOString();
      }
      
      await gradeSubmission(selectedSubmission.id, gradeData);
      setShowGradingModal(false);
      fetchAssignments(selectedProject);
      toast.success(returnForResubmission ? 'Assignment returned for resubmission with feedback' : 'Assignment graded successfully!');
    } catch (error) {
      toast.error('Error grading assignment: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.assignmentsPage}>
      <div className={styles.header}>
        <h1>Assignments</h1>
        <Button icon="+" 
          onClick={() => setShowCreateModal(true)} loading={loading}
          disabled={!selectedProject || isProjectFrozen()}
        >
          Create Assignment
        </Button>
      </div>

      {isProjectFrozen() && (
        <Card>
          <p style={{ color: '#F44336', fontWeight: 'bold', textAlign: 'center' }}>
            Alert: This project is {currentProject.status === 'on_hold' ? 'ON HOLD' : 'CANCELLED'}. 
            You cannot create or modify assignments.
          </p>
        </Card>
      )}

      <div className={styles.filterSection}>
        <div className={styles.formGroup}>
          <label>Select Project</label>
          <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className={styles.select}>
            <option value="">-- Select a Project --</option>
            {sortProjectsByName(projects).map(project => (
              <option key={project.id} value={project.id}>
                {project.student_name ? `${project.student_name} - ${project.project_name}` : project.project_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedProject ? (
        <div className={styles.assignmentsGrid}>
          {assignments.length === 0 ? (
            <Card>
              <p className={styles.empty}>No assignments created yet</p>
            </Card>
          ) : (
          assignments.map(assignment => {
            const submissions = assignmentSubmissions[assignment.id] || [];
            const hasSubmissions = submissions.length > 0;
            const latestSubmission = hasSubmissions ? submissions[0] : null;
            // Whichever outcome is already recorded; that button is then hidden
            // rather than disabled, so the row only offers the changes available.
            const currentStatus = latestSubmission?.status;
            
            return (
              <Card key={assignment.id} hover className={styles.assignmentCard}>
                <div className={styles.assignmentHeader}>
                  <h3>{assignment.title}</h3>
                  {latestSubmission && <StatusBadge status={latestSubmission.status} />}
                </div>
                <div className={styles.assignmentDetails}>
                  <AssignmentChecklist
                    description={assignment.description}
                    taskState={latestSubmission?.task_state}
                    assignmentId={assignment.id}
                    interactive={!isProjectFrozen()}
                    asMentor
                    collapsible
                  />
                  <div className={styles.dates}>
                    <p><strong>Assigned:</strong> {formatDate(assignment.assigned_date)}</p>
                    <p><strong>Deadline:</strong> {formatDate(assignment.deadline)}</p>
                  </div>
                  <div className={styles.scoreInfo}>
                    <p><strong>Max Score:</strong> {assignment.max_score > 0 ? assignment.max_score : 'Non-scorable'}</p>
                    {hasSubmissions && (
                      <p><strong>Submissions:</strong> {latestSubmission.resubmission_count + 1}</p>
                    )}
                  </div>
                </div>
                {hasSubmissions ? (
                  latestSubmission.status === 'returned' ? (
                    <div className={styles.awaitingResubmission}>
                      <span>Pending Awaiting student resubmission</span>
                    </div>
                  ) : (
                    <Button size="small" 
                      fullWidth 
                      onClick={() => handleViewSubmission(assignment)} loading={loading}
                      disabled={isProjectFrozen()}
                    >
                      {latestSubmission.status === 'completed' ? 'View Graded Work' : 'Grade Submission'}
                    </Button>
                  )
                ) : (
                  <div className={styles.noSubmission}>
                    <span>Pending Awaiting student submission</span>
                  </div>
                )}

                {/* Four actions. The three outcomes are colour-coded to match
                    their StatusBadge, so the button that was pressed and the
                    badge that results read as the same thing: green complete,
                    orange half done, red not done. */}
                <div className={styles.cardActions}>
                  <Button
                    size="small"
                    variant="outline"
                    onClick={() => handleOpenEdit(assignment)}
                    disabled={isProjectFrozen()}
                  >
                    Edit
                  </Button>
                  {currentStatus !== 'completed' && (
                    <Button
                      size="small"
                      onClick={() => handleOpenComplete(assignment)}
                      disabled={isProjectFrozen()}
                      style={{ background: OUTCOME_COLORS.completed, borderColor: OUTCOME_COLORS.completed }}
                    >
                      Mark as Completed
                    </Button>
                  )}
                  {currentStatus !== 'half_done' && (
                    <Button
                      size="small"
                      onClick={() => handleOpenOutcome(assignment, 'half_done')}
                      disabled={isProjectFrozen()}
                      style={{ background: OUTCOME_COLORS.half_done, borderColor: OUTCOME_COLORS.half_done }}
                    >
                      Half Done
                    </Button>
                  )}
                  {currentStatus !== 'not_done' && (
                    <Button
                      size="small"
                      onClick={() => handleOpenOutcome(assignment, 'not_done')}
                      disabled={isProjectFrozen()}
                      style={{ background: OUTCOME_COLORS.not_done, borderColor: OUTCOME_COLORS.not_done }}
                    >
                      Not Done
                    </Button>
                  )}
                </div>
              </Card>
            );
          })
        )}
      </div>
      ) : (
        <Card>
          <p className={styles.empty}>Please select a project to view assignments</p>
        </Card>
      )}

      {/* Create Assignment Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create New Assignment" closeOnOverlayClick={false}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Title *</label>
            <input name="title" value={values.title} onChange={handleChange} placeholder="Assignment title..." required />
          </div>
          <div className={styles.formGroup}>
            <label>Description</label>
            <textarea name="description" value={values.description} onChange={handleChange} rows="5" placeholder={'Assignment details and instructions...\nTip: put each task on its own line — the student gets a checkbox per line.'} />
            <Button
              type="button"
              size="small"
              variant="outline"
              onClick={() => handleChange({
                target: {
                  name: 'description',
                  value: `${values.description}${values.description && !values.description.endsWith('\n') ? '\n' : ''}- `,
                },
              })}
            >
              + Add checklist item
            </Button>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Assigned Date *</label>
              <input type="date" name="assigned_date" value={values.assigned_date} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Deadline *</label>
              <input type="date" name="deadline" value={values.deadline} onChange={handleChange} required />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Link to Class *</label>
            <select name="class_id" value={values.class_id} onChange={handleChange} required>
              <option value="">-- Select a Class --</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>
                  Class {cls.class_number} - {formatDate(cls.class_date)} at {cls.class_time}
                </option>
              ))}
            </select>
            <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Assignment must be linked to a class session
            </small>
          </div>
          <div className={styles.formGroup}>
            <label>Maximum Score</label>
            <input 
              type="number" 
              name="max_score" 
              value={values.max_score} 
              onChange={handleChange} 
              min="0"
              placeholder="Enter 0 for non-scorable assignment"
            />
            <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Enter 0 for non-scorable assignments, or enter a number for scorable assignments
            </small>
          </div>
          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} loading={loading}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>Create Assignment</Button>
          </div>
        </form>
      </Modal>

      {/* Not done / half done. A note instead of a score — the mentor is
          saying what is missing, not grading what is there. */}
      {outcomeValues && (
        <Modal
          isOpen
          onClose={() => setOutcomeValues(null)}
          title={`Mark as ${OUTCOME_LABELS[outcomeValues.status]}`}
        >
          <div className={styles.formGroup}>
            <p>
              <strong>{outcomeValues.assignment.title}</strong>
            </p>
            <label>Note for the student (optional)</label>
            <textarea
              rows="3"
              value={outcomeValues.note}
              onChange={(e) => setOutcomeValues((prev) => ({ ...prev, note: e.target.value }))}
              placeholder={outcomeValues.status === 'half_done'
                ? 'What is still missing?'
                : 'Anything to record about why?'}
            />
          </div>
          <div className={styles.modalActions}>
            <Button type="button" variant="outline" onClick={() => setOutcomeValues(null)} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleMarkOutcome}
              loading={saving}
              style={{
                background: OUTCOME_COLORS[outcomeValues.status],
                borderColor: OUTCOME_COLORS[outcomeValues.status],
              }}
            >
              Mark as {OUTCOME_LABELS[outcomeValues.status]}
            </Button>
          </div>
        </Modal>
      )}

      {/* Edit Assignment Modal */}
      {showEditModal && editValues && (
        <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Assignment" closeOnOverlayClick={false}>
          <form onSubmit={handleUpdateAssignment} className={styles.form}>
            <div className={styles.formGroup}>
              <label>Title *</label>
              <input name="title" value={editValues.title} onChange={handleEditChange} placeholder="Assignment title..." required />
            </div>
            <div className={styles.formGroup}>
              <label>Description</label>
              <textarea name="description" value={editValues.description} onChange={handleEditChange} rows="5" placeholder={'Assignment details and instructions...\nTip: put each task on its own line — the student gets a checkbox per line.'} />
              <Button
                type="button"
                size="small"
                variant="outline"
                onClick={() => handleEditChange({
                  target: {
                    name: 'description',
                    value: `${editValues.description}${editValues.description && !editValues.description.endsWith('\n') ? '\n' : ''}- `,
                  },
                })}
              >
                + Add checklist item
              </Button>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>Assigned Date *</label>
                <input type="date" name="assigned_date" value={editValues.assigned_date} onChange={handleEditChange} required />
              </div>
              <div className={styles.formGroup}>
                <label>Deadline *</label>
                <input type="date" name="deadline" value={editValues.deadline} onChange={handleEditChange} required />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label>Link to Class *</label>
              <select name="class_id" value={editValues.class_id} onChange={handleEditChange} required>
                <option value="">-- Select a Class --</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    Class {cls.class_number} - {formatDate(cls.class_date)} at {cls.class_time}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Maximum Score</label>
              <input
                type="number"
                name="max_score"
                value={editValues.max_score}
                onChange={handleEditChange}
                min="0"
                placeholder="Enter 0 for non-scorable assignment"
              />
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Enter 0 for non-scorable assignments, or enter a number for scorable assignments
              </small>
            </div>
            <div className={styles.formActions}>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button type="submit" loading={saving}>Save Changes</Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Mark as Completed Modal */}
      {showCompleteModal && completeValues && (
        <Modal isOpen={showCompleteModal} onClose={() => setShowCompleteModal(false)} title="Mark Assignment as Completed">
          <div className={styles.gradingSection}>
            <p>
              Marking <strong>{completeValues.assignment.title}</strong> as completed will close it out for the
              student, even if nothing was submitted through COSMIC.
            </p>

            {completeValues.assignment.max_score > 0 && (
              <div className={styles.formGroup}>
                <label>Score (out of {completeValues.assignment.max_score}) *</label>
                <input
                  type="number"
                  min="0"
                  max={completeValues.assignment.max_score}
                  value={completeValues.score}
                  onChange={(e) => setCompleteValues({ ...completeValues, score: e.target.value })}
                  placeholder={`Required (0-${completeValues.assignment.max_score})`}
                  className={styles.input}
                />
              </div>
            )}

            <div className={styles.formGroup}>
              <label>Feedback / Comments</label>
              <textarea
                value={completeValues.feedback}
                onChange={(e) => setCompleteValues({ ...completeValues, feedback: e.target.value })}
                rows={4}
                placeholder="Optional note explaining why this is being marked complete..."
                className={styles.textarea}
              />
            </div>

            <div className={styles.modalActions}>
              <Button variant="outline" onClick={() => setShowCompleteModal(false)}>Cancel</Button>
              <Button onClick={handleMarkComplete} loading={saving} style={{ background: '#4CAF50' }}>
                Mark as Completed
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Grading Modal */}
      {showGradingModal && selectedSubmission && selectedAssignmentData && (
        <Modal
          isOpen={showGradingModal}
          onClose={() => setShowGradingModal(false)}
          title="Grade Assignment Submission"
          size="large"
        >
          <div className={styles.gradingModalContent}>
            {/* Assignment Info */}
            <div className={styles.assignmentInfoBox}>
              <h3>{selectedAssignmentData.title}</h3>
              <AssignmentChecklist
                description={selectedAssignmentData.description}
                taskState={selectedSubmission?.task_state}
                assignmentId={selectedAssignmentData.id}
              />
              <div className={styles.scoreDisplay}>
                <span><strong>Max Score:</strong> {selectedAssignmentData.max_score > 0 ? selectedAssignmentData.max_score : 'Non-scorable'}</span>
                <StatusBadge status={selectedSubmission.status} />
              </div>
            </div>

            {/* Student Submission */}
            <div className={styles.submissionSection}>
              <h4>Student's Submission</h4>
              <div className={styles.submissionMeta}>
                <span><strong>Submitted:</strong> {formatDateTime(selectedSubmission.submission_date)}</span>
                <span><strong>Attempt:</strong> #{selectedSubmission.resubmission_count + 1}</span>
              </div>
              
              {selectedSubmission.submission_urls && selectedSubmission.submission_urls.length > 0 && (
                <div className={styles.urlsList}>
                  <strong>Submitted URLs:</strong>
                  {selectedSubmission.submission_urls.map((urlItem, idx) => (
                    <div key={idx} className={styles.urlItem}>
                      <div className={styles.urlTitle}>{urlItem.title}</div>
                      <a href={urlItem.url} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
                        Link: {urlItem.url}
                      </a>
                    </div>
                  ))}
                </div>
              )}
              
              {selectedSubmission.submission_notes && (
                <div className={styles.notesBox}>
                  <strong>Student's Notes:</strong>
                  <p>{selectedSubmission.submission_notes}</p>
                </div>
              )}
            </div>

            {/* Grading Form */}
            {selectedSubmission.status !== 'completed' && (
              <div className={styles.gradingSection}>
                <h4>Grade Submission</h4>
                
                {selectedAssignmentData.max_score > 0 && (
                  <div className={styles.formGroup}>
                    <label>Score (out of {selectedAssignmentData.max_score}) {gradeValues.return_for_resubmission ? '(Optional)' : '*'}</label>
                    <input
                      type="number"
                      min="0"
                      max={selectedAssignmentData.max_score}
                      value={gradeValues.score}
                      onChange={(e) => setGradeValues({...gradeValues, score: e.target.value})}
                      placeholder={gradeValues.return_for_resubmission ? 'Optional: Give partial credit' : `Required (0-${selectedAssignmentData.max_score})`}
                      className={styles.input}
                    />
                    {gradeValues.return_for_resubmission && (
                      <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        You can give partial credit even when returning for resubmission
                      </small>
                    )}
                  </div>
                )}
                
                <div className={styles.formGroup}>
                  <label>Feedback / Comments *</label>
                  <textarea
                    value={gradeValues.feedback}
                    onChange={(e) => setGradeValues({...gradeValues, feedback: e.target.value})}
                    rows={5}
                    placeholder="Provide feedback to the student..."
                    className={styles.textarea}
                    required
                  />
                </div>
                
                <div className={styles.returnSection}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={gradeValues.return_for_resubmission}
                      onChange={(e) => setGradeValues({...gradeValues, return_for_resubmission: e.target.checked})}
                    />
                    <span>Return for resubmission (don't complete yet)</span>
                  </label>
                  
                  {gradeValues.return_for_resubmission && (
                    <div className={styles.deadlineOptions}>
                      <div className={styles.formGroup}>
                        <label>New Deadline (optional)</label>
                        <input
                          type="date"
                          value={gradeValues.new_deadline}
                          onChange={(e) => setGradeValues({...gradeValues, new_deadline: e.target.value})}
                          className={styles.input}
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <small style={{ color: '#666', fontSize: '12px' }}>
                          Leave empty to keep original deadline: {formatDate(selectedAssignmentData.deadline)}
                        </small>
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.modalActions}>
                  <Button variant="outline" onClick={() => setShowGradingModal(false)} loading={loading}>
                    Cancel
                  </Button>
                  {gradeValues.return_for_resubmission ? (
                    <Button onClick={() => handleGradeSubmission(true)} loading={loading}
                      style={{ background: '#9C27B0' }}
                    >
                      Return for Resubmission
                    </Button>
                  ) : (
                    <Button onClick={() => handleGradeSubmission(false)} loading={loading}
                      style={{ background: '#4CAF50' }}
                    >
                      Complete & Return with{selectedAssignmentData.max_score > 0 ? ' Score' : 'out Score'}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* If already graded, show read-only view */}
            {selectedSubmission.status === 'completed' && (
              <div className={styles.gradedView}>
                <h4>Grading Complete</h4>
                {selectedAssignmentData.max_score > 0 && (
                  <div className={styles.finalScore}>
                    <span>Final Score:</span>
                    <span className={styles.scoreValue}>{selectedSubmission.score}/{selectedAssignmentData.max_score}</span>
                  </div>
                )}
                {selectedSubmission.feedback && (
                  <div className={styles.feedbackDisplay}>
                    <strong>Your Feedback:</strong>
                    <p>{selectedSubmission.feedback}</p>
                  </div>
                )}
                <div className={styles.gradedDate}>
                  Graded on: {formatDateTime(selectedSubmission.reviewed_at)}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MentorAssignments;

